/**
 * Pure helpers for the linear charging bar. Kept free of React/MUI so the
 * arithmetic (target, ETA, elapsed, formatting) can be reasoned about and
 * reused in isolation.
 */

/**
 * Infer the target energy (kWh) from the charged amount and the reported
 * percentage. Returns null when percent is not yet meaningful (<= 0).
 */
export const deriveTargetKwh = (
  chargedKwh: number | null | undefined,
  percent: number | null | undefined,
): number | null => {
  if (chargedKwh == null || percent == null || percent <= 0) return null;
  return chargedKwh / (percent / 100);
};

/**
 * Estimate minutes remaining to reach the target. Returns null when it cannot
 * be computed (no/low power, nothing remaining, missing inputs).
 */
export const estimateRemainingMinutes = (
  remainingKwh: number | null | undefined,
  powerKw: number | null | undefined,
): number | null => {
  if (
    remainingKwh == null ||
    powerKw == null ||
    powerKw <= 0 ||
    remainingKwh <= 0
  ) {
    return null;
  }
  return Math.ceil((remainingKwh / powerKw) * 60);
};

/** Whole minutes elapsed between a start ISO timestamp and `now`. */
export const elapsedMinutes = (
  startedAt: string | null | undefined,
  now: number = Date.now(),
): number | null => {
  if (!startedAt) return null;
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) return null;
  return Math.max(0, Math.floor((now - start) / 60000));
};

/** Clamp a percentage into the 0–100 range for bar rendering. */
export const clampPercent = (percent: number | null | undefined): number => {
  if (percent == null || Number.isNaN(percent)) return 0;
  return Math.min(100, Math.max(0, percent));
};

export const formatKw = (powerKw: number | null | undefined): string | null =>
  powerKw == null ? null : `${powerKw.toFixed(1)} kW`;

export const formatKwh = (value: number | null | undefined): string =>
  value == null ? '0.0' : value.toFixed(1);

/** Format a minute count as "1 h 05 min" / "18 min". */
export const formatDuration = (minutes: number | null | undefined): string | null => {
  if (minutes == null) return null;
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h} h ${m.toString().padStart(2, '0')} min`;
};
